import React, { useState } from 'react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  className?: string;
}

// Predefined color palette with good contrast and readability
const COLOR_PALETTE = [
  // Priority Colors (most common)
  { name: 'Critical', hex: '#DC2626', bg: 'bg-red-100', text: 'text-red-800' },
  { name: 'High', hex: '#EA580C', bg: 'bg-orange-100', text: 'text-orange-800' },
  { name: 'Medium', hex: '#EAB308', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  { name: 'Low', hex: '#16A34A', bg: 'bg-green-100', text: 'text-green-800' },
  
  // Additional Colors
  { name: 'Blue', hex: '#2563EB', bg: 'bg-accent-color-100', text: 'text-accent-color-800' },
  { name: 'Purple', hex: '#7C3AED', bg: 'bg-purple-100', text: 'text-purple-800' },
  { name: 'Pink', hex: '#DB2777', bg: 'bg-pink-100', text: 'text-pink-800' },
  { name: 'Indigo', hex: '#4F46E5', bg: 'bg-indigo-100', text: 'text-indigo-800' },
  { name: 'Cyan', hex: '#0891B2', bg: 'bg-cyan-100', text: 'text-cyan-800' },
  { name: 'Teal', hex: '#0D9488', bg: 'bg-teal-100', text: 'text-teal-800' },
  { name: 'Emerald', hex: '#059669', bg: 'bg-emerald-100', text: 'text-emerald-800' },
  { name: 'Lime', hex: '#65A30D', bg: 'bg-lime-100', text: 'text-lime-800' },
  { name: 'Amber', hex: '#D97706', bg: 'bg-amber-100', text: 'text-amber-800' },
  { name: 'Gray', hex: '#6B7280', bg: 'bg-gray-100', text: 'text-gray-800' },
];

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange, label, className = '' }) => {
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customColor, setCustomColor] = useState(value);

  const handlePresetColor = (hex: string) => {
    onChange(hex);
    setShowCustomPicker(false);
  };

  const handleCustomColor = () => {
    onChange(customColor);
    setShowCustomPicker(false);
  };

  const isValidHex = (hex: string) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      
      {/* Current Color Display */}
      <div className="flex items-center space-x-3">
        <div 
          className="w-8 h-8 rounded border-2 border-gray-300 dark:border-gray-600 shadow-sm"
          style={{ backgroundColor: value }}
        />
        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
          {value}
        </span>
        {/* Badge Preview */}
        <span 
          className="px-2 py-1 rounded-full text-xs font-medium"
          style={{ 
            backgroundColor: value + '20', // 20% opacity
            color: value,
            border: `1px solid ${value}40` // 40% opacity border
          }}
        >
          Preview
        </span>
      </div>

      {/* Preset Color Palette */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">Choose from preset colors:</p>
        <div className="grid grid-cols-7 gap-2">
          {COLOR_PALETTE.map((color) => (
            <button
              key={color.hex}
              type="button"
              onClick={() => handlePresetColor(color.hex)}
              className={`
                w-8 h-8 rounded border-2 transition-all duration-200 hover:scale-110 hover:shadow-md
                ${value === color.hex 
                  ? 'border-gray-900 dark:border-gray-100 shadow-lg ring-2 ring-offset-2 ring-blue-500' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }
              `}
              style={{ backgroundColor: color.hex }}
              title={`${color.name} (${color.hex})`}
            />
          ))}
        </div>
      </div>

      {/* Custom Color Option */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setShowCustomPicker(!showCustomPicker)}
          className="text-xs text-accent-color dark:text-accent-color-400 hover:text-accent-color-800 dark:hover:text-accent-color-300 underline"
        >
          {showCustomPicker ? 'Hide' : 'Show'} custom color picker
        </button>
        
        {showCustomPicker && (
          <div className="space-y-2 p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="w-8 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder="#6B7280"
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <button
                type="button"
                onClick={handleCustomColor}
                disabled={!isValidHex(customColor)}
                className="px-3 py-1 text-xs bg-accent-color text-white rounded hover:bg-accent-color-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Enter a valid hex color code (e.g., #DC2626)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPicker;
